import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IBenefits, IBenefitGroup } from '@epi-models/pages/BenefitGroups';
import {
  getOnboardingIsSecondStep,
  getOnboardingLoadBenefitPageType
} from '@epi-store/onboarding/onboarding.selectors';
import { onboardingActions } from '@epi-store/onboarding/onboarding.slice';
import { useOnboardingWizard } from '@epi-shared/hooks';
import { LoadBenefitPageTypes } from '@epi-constants/onboardingTypes';
import { BenefitType } from '@epi-constants/benefitTypes';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';
import { topupSliceActions } from '@epi-store/topup/topup.slice';
import {
  hasLunchBenefit,
  reconcileLoadBenefitPageType
} from '@epi-helpers/loadBenefitPageType';
import { fetchActiveBenefits } from '@epi-store/benefitGroupSettings/benefitGroupSettings.slice';

export const useTopup = () => {
  const dispatch = useDispatch();
  const { isOnBoardingActive, flags } = useOnboardingWizard();
  const benefitGroups = useGetBenefitGroupsQuery();
  const onboardingPageType = useSelector(getOnboardingLoadBenefitPageType);
  const isSecondStep = useSelector(getOnboardingIsSecondStep);
  const [isLoading, setIsLoading] = useState(true);

  const sortBenefitsInOrder = (benefits: IBenefits[]): IBenefits[] => {
    const orderMap = {
      lunch: 1,
      recreational: 2,
      transport: 3,
      massage: 4,
      wellbeing: 5
    };

    return [...benefits]
      .map(elem => elem.toLowerCase())
      .sort((a, b) => {
        const orderA = orderMap[a] || 999;
        const orderB = orderMap[b] || 999;
        return orderA - orderB;
      }) as IBenefits[];
  };

  useEffect(() => {
    dispatch(fetchActiveBenefits());
  }, []);

  useEffect(() => {
    if (benefitGroups?.data) {
      setIsLoading(false);
    }
  }, [benefitGroups]);

  const filterBenefits = (
    benefits: IBenefits[],
    effectivePageType = onboardingPageType
  ) => {
    if (!isOnBoardingActive) {
      return benefits;
    }
    if (effectivePageType === LoadBenefitPageTypes.Lounari) {
      return benefits.filter(benefit => benefit === BenefitType.Lunch);
    }
    if (effectivePageType === LoadBenefitPageTypes.Others) {
      return benefits.filter(benefit => benefit !== BenefitType.Lunch);
    }

    return benefits;
  };

  const getBenefitsFromBenefitGroups = (
    benefitGroups?: IBenefitGroup[]
  ): IBenefits[] => {
    if (!benefitGroups) return [];

    const benefits = Array.from(
      new Set(
        benefitGroups
          .filter(group => group.beneficiaryCount > 0)
          .flatMap(group =>
            group.benefitSettings.map(setting => setting.benefitType)
          )
      )
    ) as IBenefits[];
    return sortBenefitsInOrder(benefits);
  };

  const availableBenefits = useMemo(
    () => getBenefitsFromBenefitGroups(benefitGroups.data),
    [benefitGroups.data]
  );

  const effectivePageType = useMemo(() => {
    if (!isOnBoardingActive) {
      return onboardingPageType;
    }

    return reconcileLoadBenefitPageType(
      onboardingPageType,
      availableBenefits,
      isSecondStep,
      flags?.lunchFundsLoadingType ?? null
    );
  }, [
    availableBenefits,
    flags?.lunchFundsLoadingType,
    isOnBoardingActive,
    isSecondStep,
    onboardingPageType
  ]);

  const returnedBenefits = useMemo(
    () => filterBenefits(availableBenefits, effectivePageType),
    [availableBenefits, effectivePageType, isOnBoardingActive]
  );

  useEffect(() => {
    if (!isOnBoardingActive || !benefitGroups.data) {
      return;
    }

    if (effectivePageType !== onboardingPageType) {
      dispatch(onboardingActions.setLoadBenefitPageType(effectivePageType));
    }

    if (!hasLunchBenefit(availableBenefits) && isSecondStep) {
      dispatch(onboardingActions.setIsSecondStepDisplayed(false));
    }
  }, [
    availableBenefits,
    benefitGroups.data,
    dispatch,
    effectivePageType,
    isOnBoardingActive,
    isSecondStep,
    onboardingPageType
  ]);

  useEffect(() => {
    dispatch(topupSliceActions.setActiveBenefits(returnedBenefits));
  }, [returnedBenefits]);

  return {
    hasLoadableBenefit: !!returnedBenefits.length,
    isLoading,
    activeBenefits: returnedBenefits
  };
};
